import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Bell, BellSlash, CheckCircle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import usePushSubscription from '../../hooks/usePushSubscription';

/**
 * Compact card for the admin Dashboard. Shows the current push state and
 * offers Enable / Disable / Send-test-notification buttons.
 */
export default function PushToggleCard() {
  const { supported, permission, subscribed, loading, error, enable, disable, test } =
    usePushSubscription();

  const handleEnable = async () => {
    const r = await enable();
    if (r.ok) toast.success('Notifications push activées');
    else toast.error(error || 'Impossible d\'activer les notifications');
  };

  const handleDisable = async () => {
    const r = await disable();
    if (r.ok) toast.info('Notifications push désactivées');
  };

  const handleTest = async () => {
    const r = await test();
    if (r.ok) toast.success(`Notification envoyée (${r.data?.sent || 0})`);
    else toast.error(error || 'Test impossible');
  };

  if (!supported) {
    return (
      <Card className="border border-[#E2E8F0] shadow-none" data-testid="push-toggle-card">
        <CardContent className="py-3 flex items-center gap-3">
          <div className="p-1.5 rounded-md bg-slate-100">
            <BellSlash size={18} className="text-slate-500" weight="duotone" />
          </div>
          <div className="text-sm">
            <p className="font-outfit font-semibold text-[#0F172A]">Notifications push</p>
            <p className="text-xs text-[#64748B]">
              Non supporté par ce navigateur (utilisez Chrome, Edge ou Firefox récent).
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isDenied = permission === 'denied';

  return (
    <Card
      className={`border shadow-none ${
        subscribed
          ? 'border-emerald-200 bg-emerald-50/40'
          : isDenied
          ? 'border-red-200 bg-red-50/40'
          : 'border-sky-200 bg-sky-50/40'
      }`}
      data-testid="push-toggle-card"
    >
      <CardHeader className="pb-2">
        <CardTitle className="font-outfit text-base flex items-center gap-2">
          <div
            className={`p-1.5 rounded-md ${
              subscribed ? 'bg-emerald-100' : isDenied ? 'bg-red-100' : 'bg-sky-100'
            }`}
          >
            {subscribed ? (
              <Bell size={18} className="text-emerald-700" weight="duotone" />
            ) : isDenied ? (
              <Warning size={18} className="text-red-700" weight="duotone" />
            ) : (
              <Bell size={18} className="text-sky-700" weight="duotone" />
            )}
          </div>
          Notifications push
          {subscribed && (
            <span
              className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"
              data-testid="push-status-badge"
            >
              <CheckCircle size={14} weight="fill" /> Actives
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        <p className="text-xs font-manrope text-[#475569]">
          {subscribed
            ? 'Vous recevez une notification instantanée sur ce navigateur à chaque paiement GeniusPay confirmé ou alerte critique.'
            : isDenied
            ? 'Les notifications sont bloquées dans les préférences du navigateur. Autorisez le site dans les paramètres pour les réactiver.'
            : 'Activez pour recevoir instantanément les alertes de paiement Wave/Orange/MTN et les événements critiques directement sur ce téléphone/ordinateur.'}
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          {!subscribed && !isDenied && (
            <Button
              size="sm"
              onClick={handleEnable}
              disabled={loading}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="push-enable-btn"
            >
              {loading ? '…' : 'Activer les notifications'}
            </Button>
          )}
          {subscribed && (
            <>
              <Button
                size="sm"
                onClick={handleTest}
                disabled={loading}
                variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="push-test-btn"
              >
                {loading ? '…' : 'Envoyer un test'}
              </Button>
              <Button
                size="sm"
                onClick={handleDisable}
                disabled={loading}
                variant="ghost"
                className="text-red-600 hover:bg-red-50"
                data-testid="push-disable-btn"
              >
                Désactiver
              </Button>
            </>
          )}
        </div>
        {error && (
          <p className="text-xs text-red-600 mt-1" data-testid="push-error">{error}</p>
        )}
      </CardContent>
    </Card>
  );
}
